import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Sparkles, Wallet, FileText, LogOut, Search, Globe2, Vault, Bell, Crown, BarChart3, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { useEffect } from "react";
import VoiceAssistant from "@/components/common/VoiceAssistant";

const NAV = [
    { to: "/app/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/app/grants", label: "Grant matches", icon: Sparkles },
    { to: "/app/live", label: "Live grants.gov", icon: Globe2 },
    { to: "/app/claims", label: "Money owed", icon: Wallet },
    { to: "/app/applications", label: "Applications", icon: FileText },
    { to: "/app/vault", label: "My vault", icon: Vault },
    { to: "/app/affidavits", label: "Affidavits", icon: ScrollText },
];

export default function AppShell() {
    const { user, logout, unread, refreshNotifications, isAdmin } = useApp();
    const navigate = useNavigate();

    useEffect(() => {
        const id = setInterval(() => refreshNotifications(), 25000);
        return () => clearInterval(id);
    }, [refreshNotifications]);

    const initials = (user?.name || "You").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
    const isPremium = user?.tier === "premium";
    const navItems = isAdmin ? [...NAV, { to: "/app/admin", label: "Owner", icon: BarChart3 }] : NAV;

    return (
        <div className="min-h-screen bg-background flex">
            <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card sticky top-0 h-screen">
                <Link to="/" className="flex items-center gap-2 px-6 h-16 border-b border-border">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18"/><path d="M17 7H9.5a3.5 3.5 0 0 0 0 7H14a3.5 3.5 0 0 1 0 7H6"/></svg>
                    </span>
                    <span className="font-serif text-xl tracking-tight">Owed</span>
                    {isPremium && <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-accent-foreground bg-accent/20 border border-accent/40 rounded-full px-2 py-0.5"><Crown className="h-3 w-3" /> Pro</span>}
                </Link>

                <nav className="flex-1 px-3 py-6 flex flex-col gap-1">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            className={({ isActive }) =>
                                cn(
                                    "group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                                    isActive ? "bg-primary text-primary-foreground shadow-soft" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                                )
                            }
                        >
                            <item.icon className="h-4 w-4" />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>

                {!isPremium && (
                    <div className="p-4 border-t border-border">
                        <div className="rounded-xl bg-gradient-warm p-4 relative overflow-hidden">
                            <div className="absolute -top-4 -right-4 h-16 w-16 rounded-full bg-accent/20 blur-xl" />
                            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Premium</p>
                            <h4 className="font-serif text-lg mt-1 leading-tight">One-time $5</h4>
                            <p className="text-xs text-muted-foreground mt-1">Unlimited AI prep & packets. Nothing costs more than $5.</p>
                            <Button asChild size="sm" className="mt-3 w-full bg-foreground text-background hover:bg-foreground/90 rounded-full"><Link to="/app/premium">Upgrade</Link></Button>
                        </div>
                    </div>
                )}

                <div className="px-4 py-4 border-t border-border flex items-center gap-3">
                    <Avatar className="h-9 w-9"><AvatarFallback className="bg-accent text-accent-foreground font-semibold text-xs">{initials}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{user?.name || "Guest"}</p>
                        <p className="text-xs text-muted-foreground truncate">{user?.email || "—"}</p>
                    </div>
                    <button onClick={() => { logout(); navigate("/"); }} className="text-muted-foreground hover:text-foreground" title="Sign out">
                        <LogOut className="h-4 w-4" />
                    </button>
                </div>
            </aside>

            <div className="flex-1 min-w-0 flex flex-col">
                <header className="sticky top-0 z-30 bg-background/85 backdrop-blur-md border-b border-border h-16 flex items-center px-5 sm:px-8 gap-3">
                    <Link to="/" className="lg:hidden flex items-center gap-2">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
                            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18"/><path d="M17 7H9.5a3.5 3.5 0 0 0 0 7H14a3.5 3.5 0 0 1 0 7H6"/></svg>
                        </span>
                        <span className="font-serif text-lg">Owed</span>
                    </Link>

                    <div className="hidden sm:flex flex-1 max-w-lg items-center gap-2 rounded-full border border-border bg-secondary/40 px-4 h-9">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <Input placeholder="Search programs, credits, settlements…" className="border-0 bg-transparent h-8 focus-visible:ring-0 px-0 text-sm placeholder:text-muted-foreground" />
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <Link to="/app/inbox" className="relative p-2 rounded-full hover:bg-secondary" title="Notifications">
                            <Bell className="h-4 w-4" />
                            {unread > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-semibold inline-flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
                        </Link>
                        <Button asChild size="sm" className="rounded-full bg-foreground text-background hover:bg-foreground/90 hidden sm:inline-flex"><Link to="/app/grants">Find matches</Link></Button>
                    </div>
                </header>

                <nav className="lg:hidden flex overflow-x-auto gap-1 px-4 py-2 border-b border-border bg-card">
                    {navItems.map((item) => (
                        <NavLink key={item.to} to={item.to} className={({ isActive }) => cn("shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium", isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground bg-secondary")}>
                            <item.icon className="h-3.5 w-3.5" />
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <main className="flex-1 min-w-0 pb-28">
                    {/* pb-28 reserves ~7rem at the bottom of every authed page so
                        save bars, sticky CTAs, and last-row buttons never sit under
                        the platform-injected "Made by Emergent" badge in the
                        bottom-right corner. */}
                    <Outlet />
                </main>
            </div>
            <VoiceAssistant />
        </div>
    );
}
